import { useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { MaterialSymbol } from './icons';
import { useStore } from '../lib/useStore';

/**
 * Dismissible theme-app-embed onboarding card (App Store req 5.1.3): walks
 * the merchant through enabling the widget in the theme editor, with a deep
 * link that pre-selects the embed. Shown on the merchant's landing page —
 * Analytics for PRO, Conversations for FREE — until dismissed.
 */

// Theme app extension identifiers for the "enable the widget" deep link.
// The uid comes from extensions/chatforge-embed/shopify.extension.toml and
// the handle is the app-embed block filename (chatforge.liquid). Both are
// public identifiers (they appear in storefront HTML), not secrets.
const THEME_EMBED_UID = '3a5754f0-d9a4-7fd7-f939-e03236ad241faf1af4f5';
const SETUP_DISMISSED_KEY = 'chatforge-admin:setup-dismissed';

function themeEditorDeepLink(shop: string): string {
  return `https://${shop}/admin/themes/current/editor?context=apps&activateAppId=${THEME_EMBED_UID}/chatforge`;
}

export default function SetupCard({ style }: { style?: CSSProperties }): ReactNode {
  const { store } = useStore();
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(SETUP_DISMISSED_KEY) === '1';
    } catch {
      return false;
    }
  });

  if (dismissed) return null;

  const dismiss = (): void => {
    setDismissed(true);
    try {
      window.localStorage.setItem(SETUP_DISMISSED_KEY, '1');
    } catch {
      // Private mode — the card just reappears next session.
    }
  };

  return (
    <section className="cg-panel" style={style}>
      <div className="cg-panel-icon-head">
        <MaterialSymbol name="storefront" />
        <h2>Add the chat widget to your storefront</h2>
      </div>
      <p className="cg-panel-sub" style={{ marginTop: 0 }}>
        ChatForge works through a theme app embed — enable it once in your theme editor and the
        chat launcher appears in the bottom-right corner of every storefront page.
      </p>
      <ol className="cg-panel-sub" style={{ margin: '0 0 16px', paddingLeft: 20 }}>
        <li>
          Click <strong>Open theme editor</strong> below (or go to Online Store → Themes →
          Customize).
        </li>
        <li>
          ChatForge is pre-selected under <strong>App embeds</strong> in the left sidebar — make
          sure the <strong>ChatForge live chat</strong> toggle is on.
        </li>
        <li>Click <strong>Save</strong>, then visit your storefront to see the chat launcher.</li>
      </ol>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        {store ? (
          <a
            className="cg-btn-primary"
            style={{ textDecoration: 'none' }}
            href={themeEditorDeepLink(store.shop)}
            target="_blank"
            rel="noreferrer"
          >
            <MaterialSymbol name="open_in_new" size={16} />
            Open theme editor
          </a>
        ) : null}
        <button type="button" className="cg-btn-outline" onClick={dismiss}>
          Done — hide this
        </button>
      </div>
    </section>
  );
}
